import type * as React from 'react'

import { clsx } from 'clsx'

import {
  AllPostsIcon,
  ChartColumnIcon,
  Flex,
  Heading,
  IconButton,
  InfoIcon,
  Link,
  MessageSquareIcon,
  Popover,
  Text,
  TrendingUpIcon,
  UserRoundCheckIcon,
  VisuallyHidden,
} from '@buffer-mono/popcorn'
import type { IconProps } from '@buffer-mono/popcorn'

import type { DifferenceMode } from '~publish/pages/Insights/graphqlEnums'
import type { SummaryMetricItem } from '~publish/pages/Insights/components'
import { formatNumber } from '~publish/pages/Insights/utils'

import { Card } from '../Card/Card'

import styles from './WeeklyPulse.module.css'

import type { JSX } from 'react'

const HELP_CENTER_URL =
  'https://support.buffer.com/articles/understanding-sent-post-metrics-within-buffers-publish-dashboard-kppgBDLK6y'

const METRIC_ICONS: Record<string, React.ComponentType<IconProps>> = {
  postCount: AllPostsIcon,
  followers: UserRoundCheckIcon,
  comments: MessageSquareIcon,
}

function formatValue(value: number, unit: string): string {
  if (unit === 'percentage') {
    return `${value}%`
  }
  return formatNumber(value)
}

function formatDifference(diff: number, mode: DifferenceMode): string {
  if (mode === 'absolute') {
    const rounded = Math.round(Math.abs(diff))
    if (diff > 0) return `+${rounded}`
    if (diff < 0) return `-${rounded}`
    return '0'
  }
  const rounded = Number(Math.abs(diff).toFixed(1))
  if (diff > 0) return `+${rounded}%`
  if (diff < 0) return `-${rounded}%`
  return '0%'
}

interface WeeklyPulseCardProps {
  metric: SummaryMetricItem
  comparisonLabel: string
}

export function WeeklyPulseCard({
  metric,
  comparisonLabel,
}: WeeklyPulseCardProps): JSX.Element {
  const label = metric.name ?? metric.displayName ?? metric.type
  const MetricIcon = METRIC_ICONS[metric.type] ?? ChartColumnIcon

  const difference = metric.difference ?? 0
  const isPositive = difference > 0
  const isNegative = difference < 0
  const hasTrend = isPositive || isNegative

  return (
    <Card asChild radius="lg">
      <Flex direction="column" gap="xs" className={styles.card}>
        <Flex direction="row" align="center" justify="between" fullWidth>
          <Text size="sm" weight="medium" color="subtle">
            {label}
          </Text>
          <Popover>
            <Popover.Trigger>
              <IconButton
                type="button"
                label={`About ${label}`}
                size="small"
                variant="tertiary"
              >
                <InfoIcon color="subtle" size="xsmall" />
              </IconButton>
            </Popover.Trigger>
            <Popover.Content
              side="top"
              align="center"
              arrow
              className={styles.popoverContent}
            >
              <Flex direction="column" gap="xs">
                <Heading size="xs" as="h2" icon={<MetricIcon size="small" />}>
                  {label}
                </Heading>
                <Text color="subtle">{metric.description}</Text>
                <Text color="subtle">
                  Learn more on our{' '}
                  <Link
                    href={HELP_CENTER_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.helpCenterLink}
                  >
                    Help Center
                  </Link>
                  .
                </Text>
              </Flex>
            </Popover.Content>
          </Popover>
        </Flex>
        <div className={styles.statRow}>
          <Heading as="p" size="lg">
            {formatValue(metric.value, metric.unit)}
          </Heading>
          {hasTrend && (
            <>
              <TrendingUpIcon
                size="small"
                color={isPositive ? 'success' : 'critical'}
                className={clsx(styles.trendIcon, isNegative && styles.flipped)}
                aria-hidden
              />
              <Text
                size="sm"
                color="subtle"
                className={clsx(styles.periodText, styles.periodTextWithIcon)}
              >
                <VisuallyHidden>{isPositive ? 'Up ' : 'Down '}</VisuallyHidden>
                {formatDifference(
                  difference,
                  metric.differenceMode ?? 'percentage',
                )}{' '}
                · {comparisonLabel}
              </Text>
            </>
          )}
        </div>
      </Flex>
    </Card>
  )
}
